import type { BoardMove, BoardMoveCheck, BoardPlacement } from "./Board.types";
import {
    getCellEntries,
    getCellKey,
    type BoardCellEntry,
    type BoardCells,
} from "./boardCells";
import type { BoardMovedItemGetter } from "./boardSort.types";

/**
 * Where the dragged copy would land: a cell and an index among that cell's
 * entries without the dragged copy. Sorted mode ignores the index.
 */
export interface BoardDropTarget {
    placement: BoardPlacement;
    index: number;
}

/** A drag step: its target, and the reason it skipped spots `canPlaceAt` refuses. */
export interface BoardMoveTo {
    target: BoardDropTarget | null;
    skippedRefusal?: string;
}

export interface BoardEntryLocation<TItem> {
    entry: BoardCellEntry<TItem>;
    index: number;
}

export interface BoardDropResolution<TItem> {
    /** Cells with the dragged copy moved to the target, for rendering. */
    cells: BoardCells<TItem>;
    /** Null when the drop changes nothing. */
    move: BoardMove<TItem> | null;
}

interface ResolveBoardDropArgs<TItem> {
    cells: BoardCells<TItem>;
    activePlacementKey: string;
    target: BoardDropTarget;
    compareItems?: (first: TItem, second: TItem) => number;
    getMovedItem?: BoardMovedItemGetter<TItem>;
}

export const locateBoardEntry = <TItem>(
    cells: BoardCells<TItem>,
    placementKey: string,
): BoardEntryLocation<TItem> | null => {
    for (const entries of cells.entriesByCell.values()) {
        const index = entries.findIndex(
            (entry) => entry.placementKey === placementKey,
        );
        if (index !== -1) {
            return { entry: entries[index], index };
        }
    }
    return null;
};

/** The cell's entries as they read while `placementKey` is lifted out. */
export const getEntriesWithout = <TItem>(
    cells: BoardCells<TItem>,
    placement: BoardPlacement,
    placementKey: string,
) =>
    getCellEntries(cells, placement).filter(
        (entry) => entry.placementKey !== placementKey,
    );

/** Where `item` goes among sorted `entries`: after every card it ties with. */
export const getSortedIndex = <TItem>(
    entries: ReadonlyArray<BoardCellEntry<TItem>>,
    item: TItem,
    compareItems: (first: TItem, second: TItem) => number,
) => {
    const index = entries.findIndex(
        (entry) => compareItems(entry.item, item) > 0,
    );
    return index === -1 ? entries.length : index;
};

/** What the order sorts a moved card by: the item as the move leaves it. */
export const getItemAfterMove = <TItem>(
    move: BoardMoveCheck<TItem>,
    getMovedItem: BoardMovedItemGetter<TItem> | undefined,
) => getMovedItem?.(move) ?? move.item;

/**
 * Moves the dragged copy to `target` and reports the move it would commit.
 * Sorted mode lands the copy at its comparator position (of the item as
 * the move leaves it) and never reorders inside its own cell; manual mode
 * lands it at the target index.
 * The moved entry keeps its placement key: it is the drag's identity.
 */
export const resolveBoardDrop = <TItem>({
    cells,
    activePlacementKey,
    target,
    compareItems,
    getMovedItem,
}: ResolveBoardDropArgs<TItem>): BoardDropResolution<TItem> => {
    const location = locateBoardEntry(cells, activePlacementKey);
    if (location === null) {
        return { cells, move: null };
    }
    const { entry } = location;
    const sourceCellKey = getCellKey(entry.placement);
    const targetCellKey = getCellKey(target.placement);
    const isSameCell = sourceCellKey === targetCellKey;
    if (isSameCell && compareItems) {
        return { cells, move: null };
    }

    const sourceEntries = getEntriesWithout(
        cells,
        entry.placement,
        activePlacementKey,
    );
    const targetEntries = isSameCell
        ? sourceEntries
        : getCellEntries(cells, target.placement);
    const index = compareItems
        ? getSortedIndex(
              targetEntries,
              getItemAfterMove(
                  {
                      item: entry.item,
                      from: entry.placement,
                      to: target.placement,
                  },
                  getMovedItem,
              ),
              compareItems,
          )
        : Math.min(Math.max(target.index, 0), targetEntries.length);
    if (isSameCell && index === location.index) {
        return { cells, move: null };
    }

    const nextTargetEntries = [
        ...targetEntries.slice(0, index),
        { ...entry, placement: target.placement },
        ...targetEntries.slice(index),
    ];
    const entriesByCell = new Map(cells.entriesByCell);
    entriesByCell.set(sourceCellKey, sourceEntries);
    entriesByCell.set(targetCellKey, nextTargetEntries);

    return {
        cells: { ...cells, entriesByCell },
        move: {
            item: entry.item,
            from: entry.placement,
            to: target.placement,
            beforeItemId: index > 0 ? targetEntries[index - 1].itemId : null,
            afterItemId:
                index < targetEntries.length
                    ? targetEntries[index].itemId
                    : null,
            isPlacedInSort: false,
        },
    };
};
